// src/hooks/use-accommodations.ts
'use client'

// Client access to the signed-in user's test accommodations (#830).
//
// The full-length SAT runner gets its flags inside the /api/sat/state payload,
// because that request already exists and the runner needs them before the
// first paint. The PRACTICE surfaces (topic practice, mini-drills, rapid fire,
// adaptive practice) have no such payload — which is exactly why reader and
// large print stopped at the runner: a student who needs a screen reader lost
// it the moment they left a mock test.
//
// This hook closes that gap over the GET the settings form already uses. It
// deliberately does NOT block rendering: questions appear immediately at
// standard size, and the accommodation applies a beat later. Gating the whole
// practice screen on this fetch would make every session slower for everyone
// to serve a minority — the opposite of an accessibility win.

import { useEffect, useState } from 'react'
import {
  type AccommodationFlags,
  DEFAULT_ACCOMMODATIONS,
  parseAccommodations,
} from '@/lib/sat/accommodations'

export type UseAccommodations = {
  accommodations: AccommodationFlags
  /** False until the fetch settles; surfaces can defer an auto-zoom on it. */
  loaded: boolean
}

/**
 * Fetch the current user's accommodation flags once on mount.
 *
 * Failure is silent and non-fatal: on a 401 (signed out), a network error, or a
 * malformed body the hook reports the defaults, so practice still works — it
 * just works unaccommodated, which is the same as today's behaviour rather
 * than a regression.
 */
export function useAccommodations(): UseAccommodations {
  const [accommodations, setAccommodations] = useState<AccommodationFlags>({
    ...DEFAULT_ACCOMMODATIONS,
  })
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const r = await fetch('/api/account/accommodations', { cache: 'no-store' })
        if (!r.ok) return
        const body = (await r.json()) as { accommodations?: unknown }
        if (cancelled) return
        // parseAccommodations is the same defensive coercion the server uses,
        // so a partial or drifted payload can't produce undefined flags.
        setAccommodations(parseAccommodations(body?.accommodations))
      } catch {
        // Silent: practice must never break because this lookup failed.
      } finally {
        if (!cancelled) setLoaded(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  return { accommodations, loaded }
}
